import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CalendarDays, ChevronLeft, Clock3, Facebook, Link as LinkIcon, Share2, Tag, User } from 'lucide-react';
import { getImageUrl } from '@/services/api';
import { newsAPI } from '@/services/newsService';

const NewsDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [news, setNews] = useState(null);
  const [relatedNews, setRelatedNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [detailRes, listRes] = await Promise.all([
          newsAPI.getPublicById(id),
          newsAPI.listPublic(),
        ]);
        const detail = detailRes.data || null;
        const all = listRes.data || [];
        setNews(detail);
        setRelatedNews(all.filter((item) => item.id !== detail?.id).slice(0, 6));
      } catch (error) {
        console.error('Load news detail error:', error);
        setNews(null);
        setRelatedNews([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-500">กำลังโหลด...</div>;
  if (!news) return <div className="min-h-screen flex items-center justify-center text-gray-500">ไม่พบบทความ</div>;

  const image = getImageUrl(news.cover_image_url);
  const date = news.publish_date
    ? new Date(news.publish_date).toLocaleDateString('th-TH', { year: 'numeric', month: 'long', day: 'numeric' })
    : '-';
  const plainTextLength = String(news.content || news.excerpt || '').replace(/<[^>]*>/g, ' ').trim().length;
  const readMinutes = Math.max(1, Math.ceil(plainTextLength / 900));
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      alert('คัดลอกลิงก์แล้ว');
    } catch (e) {
      console.error(e);
      alert('คัดลอกลิงก์ไม่สำเร็จ');
    }
  };

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,#f1ebff_0%,#f7f5fc_40%,#f7f7fb_100%)] pb-8">
      <div className="h-20 md:h-24" />
      {image && (
        <div className="w-full mb-6">
          <div className="relative w-full h-[280px] md:h-[520px]">
            <img src={image} alt={news.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#120c20]/45 via-transparent to-transparent" />
          </div>
        </div>
      )}
      <div className="max-w-[1320px] mx-auto px-4 md:px-6 lg:px-8">
        <button
          onClick={() => navigate('/news')}
          className="inline-flex items-center gap-1.5 text-[#6a50a8] hover:text-[#412778] text-sm font-medium mb-4"
        >
          <ChevronLeft className="w-4 h-4" />
          กลับไปหน้าข่าวสาร
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">
          <article className="lg:col-span-8 bg-white/95 backdrop-blur-sm border border-[#e7defa] overflow-hidden rounded-lg shadow-[0_18px_38px_rgba(45,24,86,0.10)]">
            {!image && (
              <div className="w-full h-[200px] bg-gradient-to-r from-[#f4efff] to-[#f3fff8]" />
            )}

            <div className="px-5 md:px-8 py-6 md:py-8 border-b border-[#f0ecfa]">
              <div className="mb-3">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white text-[#5d3ea8] border border-[#ddd1f5] px-3 py-1 text-xs">
                  <Tag className="w-3.5 h-3.5" />
                  {news.category || 'ข่าวสาร'}
                </span>
              </div>
              <h1 className="text-3xl md:text-[42px] font-bold text-[#1f1a33] leading-[1.15] tracking-[-0.01em]">{news.title}</h1>

              <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                  <span className="inline-flex items-center gap-1.5">
                    <User className="w-4 h-4 text-[#5d3ea8]" />
                    {news.author || 'ทีมงาน SACIT'}
                  </span>
                  <span className="text-gray-300">•</span>
                  <span className="inline-flex items-center gap-1.5">
                    <CalendarDays className="w-4 h-4 text-[#5d3ea8]" />
                    {date}
                  </span>
                  <span className="text-gray-300">•</span>
                  <span className="inline-flex items-center gap-1.5">
                    <Clock3 className="w-4 h-4 text-[#5d3ea8]" />
                    อ่านประมาณ {readMinutes} นาที
                  </span>
                </div>
                <div className="flex items-center gap-2 text-sm bg-[#fbf9ff] border border-[#ede7fb] rounded-lg px-3 py-1.5">
                  <span className="text-gray-500 mr-1">Share</span>
                  <a
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[#e3d8fb] text-[#5b35b0] hover:bg-[#f5f1ff]"
                  >
                    <Facebook className="w-4 h-4" />
                  </a>
                  <button
                    onClick={copyLink}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[#e3d8fb] text-[#5b35b0] hover:bg-[#f5f1ff]"
                  >
                    <LinkIcon className="w-4 h-4" />
                  </button>
                  <button className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[#e3d8fb] text-[#5b35b0] hover:bg-[#f5f1ff]">
                    <Share2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {news.excerpt && (
              <div className="px-5 md:px-8 py-6 bg-[#faf8ff] border-b border-[#f0ecfa]">
                <p className="text-[#46385f] leading-relaxed text-lg">{news.excerpt}</p>
              </div>
            )}

            <div className="px-5 md:px-8 py-8 md:py-10">
              <div className="news-pro-content max-w-none" dangerouslySetInnerHTML={{ __html: news.content || '<p>ยังไม่มีเนื้อหา</p>' }} />
            </div>
          </article>

          <aside className="lg:col-span-4">
            <div className="bg-white/95 backdrop-blur-sm border border-[#e7defa] rounded-lg p-5 md:p-6 lg:sticky lg:top-24 shadow-[0_14px_30px_rgba(45,24,86,0.08)]">
              <h3 className="text-xl font-bold text-[#211a39] mb-1">Top Stories</h3>
              <p className="text-sm text-gray-500 mb-4">ข่าวที่เกี่ยวข้องและน่าสนใจ</p>
              <div className="space-y-4">
                {relatedNews.map((item) => (
                  <Link key={item.id} to={`/news/${item.id}`} className="block group">
                    <div className="flex gap-3 p-2 rounded-md hover:bg-[#f8f4ff] transition">
                      <img
                        src={getImageUrl(item.cover_image_url)}
                        alt={item.title}
                        className="w-28 h-20 rounded-md object-cover bg-gray-100 border border-[#ece6fb]"
                      />
                      <div className="min-w-0">
                        <p className="text-xs text-[#6a50a8] mb-1">{item.category || 'ข่าวสาร'}</p>
                        <p className="text-sm font-semibold text-gray-800 leading-snug line-clamp-2 group-hover:text-[#5b35b0]">{item.title}</p>
                        <p className="text-xs text-gray-500 mt-1">{item.publish_date ? String(item.publish_date).slice(0, 10) : '-'}</p>
                      </div>
                    </div>
                  </Link>
                ))}
                {!relatedNews.length && (
                  <p className="text-sm text-gray-500">ยังไม่มีข่าวอื่นเพิ่มเติม</p>
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>

      <style>{`
        .news-pro-content { font-family: 'Prompt', sans-serif; color: #303443; line-height: 2; font-size: 1.05rem; }
        .news-pro-content h1,.news-pro-content h2 { font-size: 1.85rem; font-weight: 700; color: #17162a; margin: 2rem 0 1rem; line-height: 1.45; }
        .news-pro-content h3 { font-size: 1.35rem; font-weight: 700; color: #241b3e; margin: 1.7rem 0 0.7rem; }
        .news-pro-content p { margin-bottom: 1rem; }
        .news-pro-content ul,.news-pro-content ol { margin: 0.8rem 0 1rem 1.4rem; padding-left: 0.5rem; }
        .news-pro-content li { margin-bottom: 0.5rem; }
        .news-pro-content blockquote { margin: 1.2rem 0; padding: 0.9rem 1rem; border-left: 4px solid #7c5ad3; background: #f7f3ff; color: #3b3154; }
        .news-pro-content img { border-radius: 12px; margin: 1.2rem 0; }
      `}</style>
    </div>
  );
};

export default NewsDetail;
